import { T } from '@tolgee/react';
import { useTranslate } from '@tolgee/react';
import { Input as AntdInput, Menu, Space } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { ReactNode, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
// ES6
import styled, { css } from 'styled-components';

import { CrossSvg, LogoSvg, MenuSvg } from '../../assets/icons';
import CuripodDevSvg from '../../assets/icons/CuripodDevLogo';
import Logo from '../../assets/icons/Logo';
import PadlockSvg from '../../assets/icons/PadlockSvg';
import PinSvg from '../../assets/icons/PinSvg';
import theme from '../../assets/theme';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import useRouterMatch from '../../hooks/useRouterMatch';
import useSignIn from '../../hooks/useSignIn';
import Metrics from '../../services/metrics';
import { isProduction } from '../../utils/client';
import { getTrackingParams } from '../../utils/facebookTracking';
import { notUndefined } from '../../utils/general';
import { LangSelector } from '../../utils/languageSelector';
import { configureStore } from '../../utils/store';
import { zIndexes } from '../../utils/zIndexes';
import { Input } from '../_atoms/_Input';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import {
  caseStudies,
  independentResearch,
  qualityInstruction,
  testPrep,
  TopBarDropdowns,
} from '../layouts/NewAppLayout';

export const LandingPageHeader = ({
  children,
  hideBlobs,
}: {
  children?: React.ReactNode;
  hideBlobs?: boolean;
}) => {
  const isDesktop = useBreakPoint(1300);

  if (isDesktop)
    return (
      <CssOverwriteWrapper>
        <DesktopNav hideBlobs={hideBlobs}>{children}</DesktopNav>
      </CssOverwriteWrapper>
    );
  return (
    <CssOverwriteWrapper>
      <MobileNav hideBlobs={hideBlobs}>{children}</MobileNav>
    </CssOverwriteWrapper>
  );
};

const PinInput = () => {
  const { t } = useTranslate();
  const [pin, setPin] = useState('');
  const isLargerThan1250 = useBreakPoint(1250);
  const isLargerThan1330 = useBreakPoint(1330);
  const isLargerThan1400 = useBreakPoint(1400);
  const isLargerThan1520 = useBreakPoint(1520);

  const handleEnterPin = (pinToUse: string) => {
    if (pinToUse) {
      window.open(`https://${configureStore().config.game.url}/join/${pinToUse}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent typing non-numeric characters
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPin(e.target.value);
  };

  return (
    <Space size={isLargerThan1400 ? 'middle' : 'small'}>
      <AntdInput.Group compact>
        <Input
          type="text"
          onPressEnter={e => handleEnterPin((e.target as HTMLInputElement).value)}
          onKeyPress={handleKeyPress}
          onChange={handleChange}
          placeholder={
            isLargerThan1520
              ? t('Enter pin to join lesson', 'Enter pin to join lesson')
              : isLargerThan1400
              ? t('Enter pin to join', 'Enter pin to join')
              : t('Enter pin', 'Enter pin')
          }
          inputStyle={{
            border: `1px solid ${theme.colors.grey}`,
            padding: isLargerThan1400 ? '5px 10px' : '4px 6px',
            borderRadius: '5px 0px 0px 5px',
            height: 35,
            marginLeft: isLargerThan1400 ? 7 : 3,
            fontSize: isLargerThan1400 ? '14px' : '12px',
            width: isLargerThan1520
              ? 'auto'
              : isLargerThan1400
              ? '130px'
              : isLargerThan1250
              ? '110px'
              : '95px',
          }}
        />
        <JoinButton
          onClick={() => handleEnterPin(pin)}
          type="ghost"
          $isCompact={!isLargerThan1400}
        >
          <T keyName="Join" defaultValue="Join" />
        </JoinButton>
      </AntdInput.Group>
      <LangSelector hideMobileVersion={!isLargerThan1250} />
    </Space>
  );
};

const JoinButton = styled(Button)<{ $isCompact?: boolean }>`
  background-color: ${theme.colors.blue};
  color: ${theme.colors.white};
  border-radius: 5px;
  font-weight: 700;
  font-size: ${({ $isCompact }) => ($isCompact ? '14px' : '16px')};
  padding: ${({ $isCompact }) => ($isCompact ? '4px 8px' : '5px 10px')};
  border: 1px solid ${theme.colors.grey};
  height: 35px;
  min-width: ${({ $isCompact }) => ($isCompact ? '50px' : 'auto')};

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.blueDark};
    color: ${theme.colors.white};
  }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 1;
  gap: 8px;

  @media (max-width: 1450px) {
    gap: 6px;
  }

  @media (max-width: 1300px) {
    gap: 4px;
  }
`;

const DesktopNav = ({
  children,
  hideBlobs,
}: {
  children?: React.ReactNode;
  hideBlobs?: boolean;
}) => {
  const session = useSession();
  const isLoggedIn = session && session.status === 'authenticated';
  const isLargerThan1400 = useBreakPoint(1450);
  const isDesktop = useBreakPoint(768);
  const router = useRouter();
  const { tenantId } = useRouterMatch();
  const { signIn } = useSignIn();

  return (
    <HeaderContainer $hideBlobs={hideBlobs}>
      <TopBar>
        <LogoRow>
          <Link href={'/'} passHref>
            <a>
              <IconWrapper color={theme.colors.orange} height={'22px'} width={'81px'}>
                {!isProduction() ? <CuripodDevSvg /> : <LogoSvg />}
              </IconWrapper>
            </a>
          </Link>
          <PinInput />
        </LogoRow>
        <LinkContainer>
          <TopBarDropdowns isLandingPage={true} isRightAligned={true} />
          {!isLoggedIn && (
            <>
              <RightSideVerticalDivider />
              <StyledLink
                style={{
                  fontWeight: 700,
                  color: theme.colors.landingPageTopbarColor,
                  cursor: 'pointer',
                  fontSize: isLargerThan1400 ? '18px' : '16px',
                }}
                onClick={() => {
                  if (!isLoggedIn) {
                    signIn({ callbackUrl: '/' });
                  }
                }}
              >
                <T keyName="Log in" defaultValue="Log in" />
              </StyledLink>
            </>
          )}
        </LinkContainer>
        <ResponsiveButtonRow>
          <>
            {session.status !== 'authenticated' && (
              <Button
                type="primary"
                styles={{
                  backgroundColor: theme.colors.orange,
                  borderRadius: '5px',
                  padding: isLargerThan1400 ? '10px 20px' : '8px 12px',
                  fontSize: isLargerThan1400 ? '16px' : '14px',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  if (!isLoggedIn) {
                    // Get tracking parameters for conversion tracking
                    const trackingParams = getTrackingParams();
                    const query: Record<string, string> = {
                      callback: `/${tenantId}/create`,
                    };

                    // Add tracking parameters if present
                    if (trackingParams.fbclid) {
                      query.fbclid = trackingParams.fbclid;
                    }
                    if (trackingParams.ttclid) {
                      query.ttclid = trackingParams.ttclid;
                    }

                    router.push({
                      pathname: '/signup',
                      query,
                    });
                  }
                }}
              >
                {isLargerThan1400 ? (
                  <T keyName="signuplong" defaultValue="Sign Up For Free" />
                ) : (
                  <T keyName="signup" defaultValue="Sign Up" />
                )}
              </Button>
            )}

            {session.status === 'authenticated' && (
              <OrangeButton
                type="primary"
                onClick={() => {
                  router.push(`/${tenantId}/lessons`);
                }}
              >
                <T keyName="Home" />
              </OrangeButton>
            )}
          </>
        </ResponsiveButtonRow>
      </TopBar>
      <div style={{ width: '100%' }}>{children && children}</div>
    </HeaderContainer>
  );
};

const RightSideVerticalDivider = styled.div`
  height: 30px;
  width: 0px;
  margin: 0 0 0 10px;
  border-left: solid 2px ${theme.colors.black};
`;

const OrangeButton = styled(Button)`
  background-color: ${theme.colors.orange};
  width: 100%;
  height: 42px;
  border-radius: 5px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  span {
    font-size: 20px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: none;
  }
`;

const MobileNav = ({
  children,
  hideBlobs,
}: {
  children: ReactNode;
  hideBlobs?: boolean;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const rootSubmenuKeys = ['sub1', 'sub2', 'solutions', 'impact-efficacy'];
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const session = useSession();
  const router = useRouter();
  const isLargerThan500 = useBreakPoint(500);
  const isLargerThan550 = useBreakPoint(550);
  const isLargerThan600 = useBreakPoint(600);
  const isLargerThan660 = useBreakPoint(660);
  const isDesktop = useBreakPoint(768);
  const isLoggedIn = session && session.status === 'authenticated';
  const { tenantId } = useRouterMatch();
  const isLoadingAuth = session && session.status === 'loading';
  const { signIn } = useSignIn();
  const onOpenChange = (keys: Array<string>) => {
    // Allow multiple submenus to be open at once for better mobile UX
    setOpenKeys(keys);
  };

  const menuItemsNotLoggedIn = [
    isLargerThan660 === false
      ? {
          key: 'signup',
          label: (
            <StyledLink>
              <Button
                styles={{
                  backgroundColor: theme.colors.orange,
                  borderRadius: '20px',
                }}
                onClick={() => {
                  // Get tracking parameters for conversion tracking
                  const trackingParams = getTrackingParams();
                  const query: Record<string, string> = {
                    callback: `/${tenantId}/create`,
                  };

                  // Add tracking parameters if present
                  if (trackingParams.fbclid) {
                    query.fbclid = trackingParams.fbclid;
                  }
                  if (trackingParams.ttclid) {
                    query.ttclid = trackingParams.ttclid;
                  }

                  router.push({
                    pathname: '/signup',
                    query,
                  });
                }}
              >
                <T keyName="Sign Up For Free" defaultValue="Sign Up For Free" />
              </Button>
            </StyledLink>
          ),
        }
      : undefined,
    isLargerThan660 === false
      ? {
          key: 'login',
          label: (
            <StyledLink
              style={{
                fontWeight: 500,
                fontSize: 18,
                cursor: 'pointer',
                color: theme.colors.white,
              }}
              onClick={() => {
                if (!isLoadingAuth) {
                  signIn({ callbackUrl: '/' });
                }
              }}
            >
              <T keyName="Log in" defaultValue="Log in" />
              <ColoredIconWrapper
                height={'20px'}
                width={'20px'}
                $backgroundColor={theme.colors.green}
              >
                <PadlockSvg />
              </ColoredIconWrapper>
            </StyledLink>
          ),
        }
      : undefined,
    {
      key: 'enter-pin',
      label: (
        <Link
          href={Metrics.withMetricsIdentifier(
            `https://${configureStore().config.game.url}`,
          )}
          passHref
        >
          <a>
            <StyledLink
              style={{
                fontWeight: 500,
                fontSize: 18,
                cursor: 'pointer',
                color: 'white',
              }}
            >
              <T keyName="Enter pin" defaultValue="Enter pin" />
              <ColoredIconWrapper
                height={'20px'}
                width={'20px'}
                $backgroundColor={theme.colors.blue}
              >
                <PinSvg />
              </ColoredIconWrapper>
            </StyledLink>
          </a>
        </Link>
      ),
    },
    {
      key: 'solutions',
      label: <T keyName="Solutions" defaultValue="Solutions" />,
      children: [
        ...qualityInstruction.map((item: any, index: number) => ({
          key: `quality-instruction-${index}`,
          label: (
            <Link href={item.link} passHref>
              {item.external ? (
                <a
                  target="__blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              ) : (
                <a onClick={() => setIsMenuOpen(false)}>
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              )}
            </Link>
          ),
        })),
        ...testPrep.map((item: any, index: number) => ({
          key: `test-prep-${index}`,
          label: (
            <Link href={item.link} passHref>
              {item.external ? (
                <a
                  target="__blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              ) : (
                <a onClick={() => setIsMenuOpen(false)}>
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              )}
            </Link>
          ),
        })),
      ],
    },
    {
      key: 'impact-efficacy',
      label: <T keyName="Impact & Efficacy" defaultValue="Impact & Efficacy" />,
      children: [
        ...caseStudies.map((item: any, index: number) => ({
          key: `case-studies-${index}`,
          label: (
            <Link href={item.link} passHref>
              {item.external ? (
                <a
                  target="__blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              ) : (
                <a onClick={() => setIsMenuOpen(false)}>
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              )}
            </Link>
          ),
        })),
        ...independentResearch.map((item: any, index: number) => ({
          key: `research-${index}`,
          label: (
            <Link href={item.link} passHref>
              <a onClick={() => setIsMenuOpen(false)}>
                <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                  <TruncatedText>{item.text}</TruncatedText>
                </StyledLink>
              </a>
            </Link>
          ),
        })),
      ],
    },
    {
      key: 'pricing',
      label: (
        <>
          <Link href="/pricing" passHref>
            <a onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <StyledLink
                $isActive={router && router.asPath === `/pricing`}
                style={{
                  fontWeight: 500,
                  fontSize: 18,
                  color: theme.colors.white,
                }}
              >
                <T keyName="Pricing" defaultValue="Pricing" />
              </StyledLink>
            </a>
          </Link>
        </>
      ),
    },
  ].filter(notUndefined);
  const menuItemsLoggedIn = [
    isLargerThan500 === false
      ? {
          key: 'lessons',
          label: (
            <Button
              onClick={() => {
                router.push(`/${tenantId}/lessons`);
              }}
              styles={{
                backgroundColor: theme.colors.orange,
                borderRadius: '5px',
              }}
            >
              <T keyName="Home" defaultValue="Home" />
            </Button>
          ),
        }
      : undefined,

    {
      key: 'enter-pin',
      label: (
        <Link
          href={Metrics.withMetricsIdentifier(
            `https://${configureStore().config.game.url}`,
          )}
          passHref
        >
          <a>
            <StyledLink
              style={{
                fontWeight: 500,
                fontSize: 18,
                cursor: 'pointer',
                color: theme.colors.white,
              }}
            >
              <T keyName="Enter pin" defaultValue="Enter pin" />
              <ColoredIconWrapper
                height={'20px'}
                width={'20px'}
                $backgroundColor={theme.colors.blue}
              >
                <PinSvg />
              </ColoredIconWrapper>
            </StyledLink>
          </a>
        </Link>
      ),
    },
    {
      key: 'solutions',
      label: <T keyName="Solutions" defaultValue="Solutions" />,
      children: [
        ...qualityInstruction.map((item: any, index: number) => ({
          key: `quality-instruction-${index}`,
          label: (
            <Link href={item.link} passHref>
              {item.external ? (
                <a
                  target="__blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              ) : (
                <a onClick={() => setIsMenuOpen(false)}>
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              )}
            </Link>
          ),
        })),
        ...testPrep.map((item: any, index: number) => ({
          key: `test-prep-${index}`,
          label: (
            <Link href={item.link} passHref>
              {item.external ? (
                <a
                  target="__blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              ) : (
                <a onClick={() => setIsMenuOpen(false)}>
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              )}
            </Link>
          ),
        })),
      ],
    },
    {
      key: 'impact-efficacy',
      label: <T keyName="Impact & Efficacy" defaultValue="Impact & Efficacy" />,
      children: [
        ...caseStudies.map((item: any, index: number) => ({
          key: `case-studies-${index}`,
          label: (
            <Link href={item.link} passHref>
              {item.external ? (
                <a
                  target="__blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              ) : (
                <a onClick={() => setIsMenuOpen(false)}>
                  <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                    <TruncatedText>{item.text}</TruncatedText>
                  </StyledLink>
                </a>
              )}
            </Link>
          ),
        })),
        ...independentResearch.map((item: any, index: number) => ({
          key: `research-${index}`,
          label: (
            <Link href={item.link} passHref>
              <a onClick={() => setIsMenuOpen(false)}>
                <StyledLink style={{ color: theme.colors.white, fontSize: 16 }}>
                  <TruncatedText>{item.text}</TruncatedText>
                </StyledLink>
              </a>
            </Link>
          ),
        })),
      ],
    },
    {
      key: 'pricing',
      label: (
        <>
          <Link href="/pricing" passHref>
            <a onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <StyledLink
                $isActive={router && router.asPath === `/pricing`}
                style={{
                  fontWeight: 500,
                  fontSize: 18,
                  color: theme.colors.white,
                }}
              >
                <T keyName="Pricing" defaultValue="Pricing" />
              </StyledLink>
            </a>
          </Link>
        </>
      ),
    },
  ].filter(notUndefined);

  return (
    <>
      <MobileTopBar isOpen={isMenuOpen}>
        <MobileRow>
          <Space size="large" align="center">
            <Link href="/" passHref>
              <IconWrapper color={theme.colors.orange} height={'22px'} width={'81px'}>
                <Logo />
              </IconWrapper>
            </Link>
            {isLargerThan550 && <LangSelector />}
          </Space>
          <ResponsiveTopBarContainer>
            <div
              style={{
                display: 'flex',
              }}
            >
              <StyledLink
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  cursor: 'pointer',
                  color: theme.colors.black,
                  marginRight: 20,
                }}
                onClick={() => {
                  if (!isLoggedIn) {
                    signIn({ callbackUrl: '/' });
                  }
                }}
              >
                {isLoggedIn ? '' : <T keyName="Log in" defaultValue="Log in" />}
              </StyledLink>
              {isLoggedIn ? (
                <Link href={`${tenantId}/lessons`} passHref>
                  <a
                    style={{
                      fontWeight: 500,
                      fontSize: 14,
                      marginRight: 20,
                    }}
                  >
                    <Button
                      styles={{
                        backgroundColor: theme.colors.orange,
                        borderRadius: '5px',
                      }}
                    >
                      <T keyName="Home" defaultValue="Home" />
                    </Button>
                  </a>
                </Link>
              ) : (
                <Button
                  styles={{
                    backgroundColor: theme.colors.orange,
                    borderRadius: '5px',
                    fontWeight: 500,
                    fontSize: 14,
                    marginRight: 20,
                    padding: '8px 12px',
                    maxWidth: isLargerThan600 ? '140px' : '80px',
                    minWidth: isLargerThan600 ? '100px' : '70px',
                  }}
                  onClick={() => {
                    // Get tracking parameters for conversion tracking
                    const trackingParams = getTrackingParams();
                    const query: Record<string, string> = {
                      callback: `/${tenantId}/create`,
                    };

                    // Add tracking parameters if present
                    if (trackingParams.fbclid) {
                      query.fbclid = trackingParams.fbclid;
                    }
                    if (trackingParams.ttclid) {
                      query.ttclid = trackingParams.ttclid;
                    }

                    router.push({
                      pathname: '/signup',
                      query,
                    });
                  }}
                >
                  {isLargerThan600 ? (
                    <T keyName="Sign Up For Free" defaultValue="Sign Up For Free" />
                  ) : (
                    <T keyName="Sign Up" defaultValue="Sign Up" />
                  )}
                </Button>
              )}
            </div>
          </ResponsiveTopBarContainer>

          <Space size="small" align="center">
            {!isLargerThan550 && <LangSelector />}
            <DrawerIconwWrapper
              color={theme.colors.black}
              height={'30px'}
              width={'50px'}
              onClick={e => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
            >
              {!isMenuOpen ? <MenuSvg /> : <CrossSvg />}
            </DrawerIconwWrapper>
          </Space>
        </MobileRow>
      </MobileTopBar>
      <StyleWrapper $isMenuOpen={isMenuOpen}>
        <CSSTransition
          in={isMenuOpen}
          timeout={600}
          key="slide"
          classNames="slide"
          unmountOnExit
        >
          <MenuContainer>
            <Expanded>
              <MenuItems>
                <Menu
                  style={{ width: 'max-content', background: 'none' }}
                  defaultOpenKeys={[]}
                  mode="inline"
                  openKeys={openKeys}
                  onOpenChange={onOpenChange}
                  theme="dark"
                  items={isLoggedIn ? menuItemsLoggedIn : menuItemsNotLoggedIn}
                />
              </MenuItems>
            </Expanded>
            <MenuOverlay onClick={() => setIsMenuOpen(false)} />
          </MenuContainer>
        </CSSTransition>
      </StyleWrapper>
      <MobileHeaderContainer $hideBlobs={hideBlobs}>
        {children && children}
      </MobileHeaderContainer>
    </>
  );
};
const ResponsiveTopBarContainer = styled.div`
  padding-left: 20px;
  flex: 1;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: end;
  @media (max-width: 550px) {
    display: none;
  }
  @media (max-width: 630px) {
    padding-left: 10px;
  }
  @media (max-width: 600px) {
    justify-content: flex-end;
    padding-left: 5px;
  }
  @media (max-width: 570px) {
    padding-left: 2px;
  }
`;

const DrawerIconwWrapper = styled(IconWrapper)`
  @media (max-height: 600px) and (orientation: landscape) {
    padding-right: 40px;
  }
`;

const HeaderContainer = styled.div<{ $hideBlobs?: boolean }>`
  position: relative;
  background: ${theme.colors.background};
  width: 100%;
  margin-top: 60px;
  ${({ $hideBlobs }) => !$hideBlobs && `background-image: url('/static/bground.svg')`};
  ${({ $hideBlobs }) => $hideBlobs && `background-color: ${theme.colors.green}`};
  background-size: cover;
`;
const MobileHeaderContainer = styled.div<{ $hideBlobs?: boolean }>`
  position: relative;
  width: 100%;
  background-color: ${theme.colors.background};
  margin-top: 60px; /* Add margin to account for fixed header */

  ${({ $hideBlobs }) =>
    !$hideBlobs && `background-image: url('/static/mobilemenubackground.svg')`};
  ${({ $hideBlobs }) => $hideBlobs && `background-color: ${theme.colors.green}`};
  background-size: cover;
`;

const StyleWrapper = styled.div<{ $isMenuOpen: boolean }>`
  top: 60px;
  left: 0;
  z-index: ${zIndexes('LANDING_PAGE_HEADER')};

  padding-top: ${({ $isMenuOpen }) => ($isMenuOpen ? '60px' : '0px')};

  /* ENTER TRANSITION */
  /* Declare transition start properties*/
  .slide-enter {
    transform: translateX(100vw);
    opacity: 0;
  }

  /* Declare transition properties */
  .slide-enter.slide-enter-active {
    transform: translateX(0);
    opacity: 1;
    transition: all 300ms cubic-bezier(0.79, 0.14, 0.15, 0.86);
  }

  /* EXIT TRANSITION */
  .slide-exit {
    transform: translateX(0);
    opacity: 1;
  }

  .slide-exit.slide-exit-active {
    transform: translateX(100vw);
    opacity: 0;
    transition: all 300ms cubic-bezier(0.79, 0.14, 0.15, 0.86);
  }
`;

const MobileRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const MenuContainer = styled.div`
  width: 100%;
  height: 100vh;
  z-index: ${zIndexes('MOBILE_TOP_NAV_LEFT_BAR')};
  position: fixed;
  background: rgba(0, 0, 0, 0.8);
  color: ${theme.colors.landingPageTopbarColor};
  padding-top: 10px;
  padding-bottom: 20px;
  right: 0px;
  top: 60px;
`;

const MenuOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const Expanded = styled.div`
  position: relative;
`;
const StyledLink = styled.span<{ $isActive?: boolean }>`
  font-weight: 500;
  display: flex;
  justify-content: right;
  align-items: center;
  padding: 10px 20px;
  max-width: 280px;

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: ${theme.colors.translucentWhite};
      font-weight: 600 !important;
      border-radius: 10px 0px 0px 10px;
    `}
`;

const TruncatedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
  display: block;
  font-weight: 400;
`;

const ColoredIconWrapper = styled(IconWrapper)<{ $backgroundColor: string }>`
  background: ${({ $backgroundColor }) => $backgroundColor};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-left: 16px;
`;

const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-right: 10px;
  & > * {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  ul {
    color: ${theme.colors.white};
  }

  .ant-menu-inline,
  .ant-menu-vertical,
  .ant-menu-vertical-left {
    border-right: none;
  }

  .ant-menu-sub {
    background: none;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: smooth;
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.grey} transparent;
    padding-right: 5px;
  }

  .ant-menu-sub::-webkit-scrollbar {
    width: 6px;
  }

  .ant-menu-sub::-webkit-scrollbar-track {
    background: transparent;
  }

  .ant-menu-sub::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.grey};
    border-radius: 3px;
  }

  .ant-menu-sub::-webkit-scrollbar-thumb:hover {
    background-color: ${theme.colors.greyLight};
  }

  .ant-menu-submenu-title {
    font-size: 18px;
    color: ${theme.colors.white};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 280px;
  }

  .ant-menu-dark .ant-menu-inline.ant-menu-sub {
    background: none;
  }

  .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
    background: none;
    border-right: none;
  }

  .ant-menu-inline > .ant-menu-item {
    height: 60px;
    padding: 5px 0px !important;
    margin: 0px !important;
  }

  .ant-menu-item,
  .ant-menu-submenu-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 300px;
  }

  .ant-menu-item > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  .ant-menu-inline.ant-menu-root .ant-menu-item > .ant-menu-title-content,
  .ant-menu-inline.ant-menu-root .ant-menu-submenu-title > .ant-menu-title-content {
    font-weight: 400;
    display: flex;
    justify-content: flex-end;
  }

  .ant-menu-dark.ant-menu-inline .ant-menu-item,
  @media (max-height: 640px) {
    .ant-menu-inline > .ant-menu-item {
      height: 50px;
    }
  }
`;

const MobileTopBar = styled.div<{ isOpen: boolean }>`
  height: 60px;
  z-index: ${zIndexes('LANDING_PAGE_HEADER')};
  width: 100%;
  flex: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${theme.colors.landingPageTopbarColor};
  background-color: ${theme.colors.landingPageTopbarBackground};
  top: 0;
  margin: auto;
  @media (max-width: 1200px) {
    padding-left: 20px;
    padding-right: 20px;
  }
  flex-wrap: wrap;
  box-shadow: ${({ isOpen }) => (isOpen ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none')};
  position: fixed;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const ResponsiveButtonRow = styled(Row)`
  height: 50px;
  min-width: 0;
  flex-shrink: 0;

  @media (max-width: 1520px) {
    height: 45px;
  }

  @media (max-width: 1400px) {
    height: 42px;
  }

  @media (max-width: 1250px) {
    height: 40px;
  }
`;
const LinkContainer = styled(Row)`
  padding-left: 10px;
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0; /* Allow flex item to shrink below content size */

  @media (max-width: 1520px) {
    padding-left: 8px;
  }

  @media (max-width: 1400px) {
    padding-left: 6px;
  }

  @media (max-width: 1250px) {
    padding-left: 4px;
  }
`;

const TopBar = styled.div`
  height: 60px;
  z-index: ${zIndexes('TOPBAR')};
  width: 100%;
  flex: 0;
  padding-left: 10px;
  padding-right: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${theme.colors.landingPageTopbarColor};
  background-color: ${theme.colors.landingPageTopbarBackground};
  position: fixed;
  top: 0;
  margin: auto;
  flex-wrap: nowrap;
  overflow: hidden;

  @media (max-width: 1520px) {
    padding-left: 15px;
    padding-right: 15px;
  }

  @media (max-width: 1400px) {
    padding-left: 12px;
    padding-right: 12px;
  }

  @media (max-width: 1250px) {
    padding-left: 8px;
    padding-right: 8px;
  }

  @media (max-width: 1200px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

{
  /* due to CMS overriding styles we need to manually override them back to normal*/
}
const CssOverwriteWrapper = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  a,
  span {
    font-family: Nunito;
  }

  p {
    font-weight: bold;
    line-height: 22px;
    font-size: 14px;
    margin-bottom: 10px;
  }
  a,
  li {
    text-decoration: unset !important;
    line-height: 20px;
  }
  h3 {
    font-size: 1.17em;
    line-height: 1.5715;
    margin-bottom: 0.5em;
    margin-top: 0;
  }
`;
